import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Menu } from "lucide-react";
import { ApogeuLogo } from "./ApogeuLogo";
import { Button } from "./Button";
import { NotificationsBell } from "./NotificationsBell";
import { SidebarNavigation } from "./SidebarNavigation";
import { TenantSwitcher } from "./TenantSwitcher";

interface ShellProps {
  readonly userName: string;
  readonly userRole: string;
  readonly ctName: string;
  readonly tabs: readonly string[];
  readonly tab: string;
  readonly onTabChange: (tab: string) => void;
  readonly onLogout: () => void;
  readonly children: ReactNode;
}

interface AccountControlsProps {
  readonly userName: string;
  readonly userRole: string;
  readonly onLogout: () => void;
}

function AccountControls({ userName, userRole, onLogout }: AccountControlsProps) {
  return (
    <div className="app-shell__account-controls">
      <span className="app-shell__role-badge">{userRole}</span>
      <span className="app-shell__user-name" title={userName}>{userName}</span>
      <TenantSwitcher />
      <Button variant="ghost" onClick={onLogout} style={{ fontSize: 11.5 }}>
        sair
      </Button>
    </div>
  );
}

export function Shell({ userName, userRole, ctName, tabs, tab, onTabChange, onLogout, children }: ShellProps) {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [restoreToggleFocus, setRestoreToggleFocus] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const drawerCloseRef = useRef<HTMLButtonElement>(null);
  const drawerOpenRef = useRef(isDrawerOpen);
  drawerOpenRef.current = isDrawerOpen;

  const closeMobileDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setRestoreToggleFocus(true);
  }, []);

  useEffect(() => {
    const navigationMediaQuery = window.matchMedia("(max-width: 767px)");
    const syncNavigationMode = () => setIsMobile(navigationMediaQuery.matches);

    syncNavigationMode();
    navigationMediaQuery.addEventListener("change", syncNavigationMode);

    return () => navigationMediaQuery.removeEventListener("change", syncNavigationMode);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setIsDrawerOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || !isDrawerOpen) {
      return;
    }

    document.body.classList.add("app-shell-drawer-open");

    return () => document.body.classList.remove("app-shell-drawer-open");
  }, [isDrawerOpen, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape" && drawerOpenRef.current) {
        event.preventDefault();
        closeMobileDrawer();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeMobileDrawer, isMobile]);

  useEffect(() => {
    if (isMobile && isDrawerOpen) {
      drawerCloseRef.current?.focus();
    }
  }, [isDrawerOpen, isMobile]);

  useEffect(() => {
    if (!isDrawerOpen && restoreToggleFocus) {
      toggleRef.current?.focus();
      setRestoreToggleFocus(false);
    }
  }, [isDrawerOpen, restoreToggleFocus]);

  const handleNavigationToggle = () => {
    if (isMobile) {
      if (isDrawerOpen) {
        closeMobileDrawer();
        return;
      }

      setIsDrawerOpen(true);
      return;
    }

    setIsSidebarExpanded(expanded => !expanded);
  };

  const handleTabSelection = (destination: string) => {
    onTabChange(destination);

    if (isMobile) {
      closeMobileDrawer();
    }
  };

  const navigationExpanded = isMobile ? isDrawerOpen : isSidebarExpanded;
  const shellStateClass = isSidebarExpanded ? "app-shell--sidebar-expanded" : "app-shell--sidebar-collapsed";
  const drawerStateClass = isDrawerOpen ? "app-shell--drawer-open" : "app-shell--drawer-closed";
  const accountControls = <AccountControls userName={userName} userRole={userRole} onLogout={onLogout} />;

  return (
    <div className={`app-shell ${shellStateClass} ${drawerStateClass}`}>
      <header className="app-shell__header">
        <button
          ref={toggleRef}
          className="app-shell__navigation-toggle"
          type="button"
          aria-label="Alternar navegação"
          aria-controls="app-shell-navigation"
          aria-expanded={navigationExpanded}
          title="Alternar navegação"
          onClick={handleNavigationToggle}
        >
          <Menu aria-hidden="true" size={20} />
        </button>
        <div className="app-shell__header-identity">
          <ApogeuLogo width={54} className="app-shell__logo" />
          {!isMobile && <span className="app-shell__header-context">{ctName}</span>}
        </div>
        <div className="app-shell__header-end">
          <NotificationsBell />
          {!isMobile && (
            <div className="app-shell__header-actions">
              {accountControls}
            </div>
          )}
        </div>
      </header>
      <div className="app-shell__workspace">
        <SidebarNavigation
          isMobile={isMobile}
          isDrawerOpen={isDrawerOpen}
          ctName={ctName}
          tabs={tabs}
          tab={tab}
          onTabSelection={handleTabSelection}
          onClose={closeMobileDrawer}
          drawerCloseRef={drawerCloseRef}
          accountControls={accountControls}
        />
        <main className="app-shell__main">
          <div className="app-shell__main-content">{children}</div>
        </main>
      </div>
    </div>
  );
}
